import {
  Palette,
  CheckCircle,
  XCircle,
  Sparkles,
  Layers,
  ArrowRight,
  ShieldAlert,
  Smartphone,
  Sliders,
} from "lucide-react";
import { Badge, Button } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

export function TestUiOverviewTab() {
  const comparisons = [
    {
      category: "1. Thẻ Chỉ Số KPI (KpiCard & KpiGrid)",
      before: "Mỗi trang tự dựng thẻ div màu sắc tùy hứng, khi bấm chọn không đổi nền, thẻ Đang tắt lại chuyển thành màu cam rực rỡ, thiếu chấm nhận diện active.",
      after: "KpiCard chuẩn Design System: viền 2px tone, chấm active indicator, nền pastel nhạt khi chưa chọn (25% opacity), saturated bg khi active, thẻ Inactive giữ đúng tone neutral xám.",
    },
    {
      category: "2. Màn hình Danh sách (List)",
      before: "Mỗi module viết bảng một kiểu, rớt dòng text, mobile bị co ép ngang, phân trang in-memory phân mảnh.",
      after: "Chuẩn DataTable Desktop (cột STT + Thao tác + cuộn ngang không rớt dòng) & Mobile Card Stack không nút vụn vặt.",
    },
    {
      category: "3. Form Tạo mới (Create Modal)",
      before: "Màn hình tạo mới mở trang riêng hoặc drawer chật hẹp, thiếu phân nhóm nghiệp vụ thực phẩm.",
      after: "EntityFormModal với FormSection hỗ trợ stepNumber (1, 2, 3): Định danh → Thông số HACCP/Lô → Dòng hàng lặp lại (SearchAppendRepeater).",
    },
    {
      category: "4. Màn hình Chi tiết (Detail)",
      before: "Dump 15-20 trường dữ liệu nằm ngang nhau, không nhận diện được ngay đơn hàng có rủi ro gì.",
      after: "Decision IA 3 tầng: Identity (Mã duy nhất) → KPI Decision Strip (nhận diện trong 5s) → Stepper tiến trình → Bố cục 8/12 + 4/12 Attention.",
    },
    {
      category: "5. Khối dữ liệu & Lưới (Blocks & Grids)",
      before: "Khoảng cách padding tùy tiện, nhãn lúc in đậm lúc in hoa, nhãn mờ nhạt khó đọc so với tiêu đề.",
      after: "DetailSectionCard & DetailFieldGrid: tiêu đề & nhãn trường chữ đậm (text-text font-semibold) đồng bộ độ tương phản, font Mono cho mã/lô, xử lý ô trống thông minh.",
    },
    {
      category: "6. Trình chọn phân cấp (TreeSelect)",
      before: "Dropdown phẳng không thể hiện được quan hệ cha con hoặc dùng dấu gạch xiên ' / ' gây nhầm lẫn đường dẫn.",
      after: "TreeSelect chuẩn MDM: Đường nhánh L chỉ dẫn trực quan, tìm kiếm tự bung cành, phân cách chuẩn bằng dấu ' > ' (Ví dụ: Nguyên liệu > Thủy hải sản).",
    },
    {
      category: "7. Trạng thái (States & Badges)",
      before: "Tự viết inline style và mã màu lung tung (xanh lá nhạt, cam đậm, đỏ viền...).",
      after: "StatusBadge duy nhất kế thừa semantic color tokens của TM Food ERP.",
    },
  ];

  return (
    <section className="space-y-6">
      {/* Intro */}
      <div>
        <h2 className="m-0 text-h2 font-bold text-text">
          Tổng Quan Quy Chuẩn Giao Diện (Design System & Consistency Standards)
        </h2>
        <p className="m-0 text-caption text-text-muted">
          Hướng dẫn áp dụng để toàn bộ các module (MES, WMS, Bán hàng, Mua hàng, Kế hoạch, QMS) đạt tính đồng bộ tuyệt đối
        </p>
      </div>

      {/* Comparison: Trước vs Sau chuẩn hóa */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center gap-2 border-b border-border/60 pb-2">
          <Sparkles className="size-5 text-primary" />
          <h3 className="m-0 text-h3 font-bold text-text">
            Bảng So Sánh Trước & Sau Khi Chuẩn Hóa Giao Diện
          </h3>
        </div>

        <div className="overflow-hidden rounded-control border border-border/70 bg-surface">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-left text-caption border-collapse">
              <thead>
                <tr className="border-b border-border bg-bg/80 text-text-secondary uppercase select-none">
                  <th className="py-2.5 px-3 w-1/4">Thành phần giao diện</th>
                  <th className="py-2.5 px-3 w-3/8 text-danger-fg">
                    Trước khi chuẩn hóa (Không đồng bộ)
                  </th>
                  <th className="py-2.5 px-3 w-3/8 text-success-fg">
                    Sau khi chuẩn hóa (Chuẩn TM Food ERP)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-body">
                {comparisons.map((c, idx) => (
                  <tr
                    key={idx}
                    className={cn(
                      "transition-colors",
                      idx % 2 === 1 ? "bg-table-stripe" : "bg-surface",
                      "hover:bg-primary-soft/60"
                    )}
                  >
                    <td className="py-3 px-3 font-semibold text-text">{c.category}</td>
                    <td className="py-3 px-3 text-text-secondary">
                      <div className="flex items-start gap-1.5 text-danger-fg">
                        <XCircle className="size-4 shrink-0 mt-0.5" />
                        <span>{c.before}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-text">
                      <div className="flex items-start gap-1.5 text-success-fg font-medium">
                        <CheckCircle className="size-4 shrink-0 mt-0.5" />
                        <span>{c.after}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Design System Tokens: Colors */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2 border-b border-border/60 pb-2">
          <Palette className="size-4 text-primary" />
          <span>Hệ Thống Màu Semantic Tokens TM Food ERP</span>
        </h3>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7 pt-1 text-caption">
          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-primary" />
            <div className="font-bold text-text">Brand Primary</div>
            <div className="font-mono text-[11px] text-text-muted">Fresh Forest (152)</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-accent" />
            <div className="font-bold text-text">Lime Accent</div>
            <div className="font-mono text-[11px] text-text-muted">Active Plant / WIP</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-success" />
            <div className="font-bold text-text">Success</div>
            <div className="font-mono text-[11px] text-text-muted">HACCP / Đạt KCS</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-warning" />
            <div className="font-bold text-text">Warning</div>
            <div className="font-mono text-[11px] text-text-muted">Cận date / CCP sắp vượt</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-danger" />
            <div className="font-bold text-text">Danger / Hold</div>
            <div className="font-mono text-[11px] text-text-muted">Tạm giữ / Lỗi ATTP</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-info" />
            <div className="font-bold text-text">Info</div>
            <div className="font-mono text-[11px] text-text-muted">Kho lạnh / Cấp đông</div>
          </div>

          <div className="rounded-control border border-border p-3 space-y-2">
            <div className="h-10 w-full rounded bg-surface border border-border/80" />
            <div className="font-bold text-text">Neutral</div>
            <div className="font-mono text-[11px] text-text-muted">Không hoạt động / Inactive</div>
          </div>
        </div>
      </div>

      {/* Button Variants Kit */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2 border-b border-border/60 pb-2">
          <Sliders className="size-4 text-primary" />
          <span>Quy Chuẩn Nút Bấm & Tương Tác (Buttons Kit Chuẩn ERP)</span>
        </h3>
        <p className="m-0 text-caption text-text-muted">
          Quy chuẩn kích thước gọn gàng: Chuẩn Compact h-8 (32px) cho bảng & form; h-9 (36px) cho thanh thao tác chính
        </p>

        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          <button
            type="button"
            className="inline-flex items-center h-8 px-3.5 text-caption font-semibold rounded-md bg-primary text-on-primary hover:bg-primary-hover transition-colors shadow-xs"
          >
            Primary (h-8)
          </button>
          <button
            type="button"
            className="inline-flex items-center h-8 px-3 text-caption font-medium rounded-md border border-border bg-surface text-text hover:bg-bg transition-colors shadow-2xs"
          >
            Secondary
          </button>
          <button
            type="button"
            className="inline-flex items-center h-8 px-3 text-caption font-medium rounded-md bg-primary-soft text-primary-fg hover:bg-primary-soft/80 transition-colors"
          >
            Soft Button
          </button>
          <button
            type="button"
            className="inline-flex items-center h-8 px-2.5 text-caption font-medium rounded-md text-text-secondary hover:text-text hover:bg-bg transition-colors"
          >
            Ghost Button
          </button>
          <button
            type="button"
            className="inline-flex items-center h-8 px-3 text-caption font-semibold rounded-md bg-danger text-on-primary hover:bg-danger-hover transition-colors shadow-xs"
          >
            Danger Button
          </button>
          <button
            type="button"
            disabled
            className="inline-flex items-center h-8 px-3 text-caption font-medium rounded-md bg-disabled-bg text-disabled-fg border border-disabled-border cursor-not-allowed"
          >
            Disabled Button
          </button>
        </div>
      </div>
    </section>
  );
}
